import { useState, type FormEvent } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import type { SubcontractorUser } from '../../../planning/types';
import { USER_MODAL_LABELS as LABELS  } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface UserModalProps {
  user?: SubcontractorUser;
  onClose: () => void;
  onSave: (firstName: string, lastName: string) => Promise<boolean>;
}

export default function UserModal({ user, onClose, onSave }: UserModalProps) {
  useModalHistory(onClose);
  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [errors, setErrors] = useState({ firstName: '', lastName: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors = {
      firstName: !firstName.trim() ? LABELS.firstNameRequired : '',
      lastName: !lastName.trim() ? LABELS.lastNameRequired : '',
    };
    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    const ok = await onSave(firstName.trim(), lastName.trim());
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={user ? LABELS.editTitle : LABELS.createTitle} confirmLabel={LABELS.save} confirmFormId="user-form" confirmDisabled={saving} confirmLoading={saving} testId="user-modal">
      <form id="user-form" onSubmit={handleSubmit} noValidate>

        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.firstNameLabel}
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: '' })); }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!errors.firstName}
            helperText={errors.firstName || undefined}
          />
          <TextField
            label={LABELS.lastNameLabel}
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: '' })); }}
            required
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName || undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
